import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Plus } from 'lucide-react';
import { translate } from '../../lib/translations';

interface Player {
  playerid: number;
  skaterfullname: string;
  positioncode: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  goals: number;
  assists: number;
  points: number;
  isnhl: boolean;
}

interface ProtectionListTableProps {
  players: Player[];
  onAddToProtection: (player: Player) => void;
  language: 'en' | 'fr';
}

interface Column {
  field: keyof Player;
  translationKey: string;
  sortable?: boolean;
  getValue?: (player: Player) => number | string;
}

interface SortConfig {
  field: keyof Player;
  direction: 'asc' | 'desc';
}

const columns: Column[] = [
  { field: 'skaterfullname', translationKey: 'name', sortable: true },
  { field: 'positioncode', translationKey: 'position' },
  { field: 'teamabbrevs', translationKey: 'team' },
  { field: 'salary', translationKey: 'salary', sortable: true },
  { field: 'yearleft', translationKey: 'years.left' },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'points', translationKey: 'points', sortable: true },
  {
    field: 'points',
    translationKey: 'ppg',
    sortable: true,
    getValue: (player) =>
      player.gamesplayed > 0
        ? Number((player.points / player.gamesplayed).toFixed(2))
        : 0,
  },
];

export function ProtectionListTable({
  players,
  onAddToProtection,
  language,
}: ProtectionListTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'points',
    direction: 'desc',
  });
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});

  const handleSort = (field: keyof Player) => {
    setSortConfig((current) => ({
      field,
      direction:
        current.field === field && current.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const renderSortIcon = (field: keyof Player, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const getCellValue = (player: Player, column: Column) => {
    if (column.getValue) {
      return column.getValue(player);
    }
    if (column.field === 'salary') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(player.salary);
    }
    return player[column.field];
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getPlayerImageUrl = (player: Player) => {
    if (imageErrors[player.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.playerid}.png`;
  };

  const sortedPlayers = [...players].sort((a, b) => {
    const aValue = sortConfig.field === 'points' && columns.find(col => col.translationKey === 'ppg')?.getValue
      ? columns.find(col => col.translationKey === 'ppg')!.getValue!(a)
      : a[sortConfig.field];
    const bValue = sortConfig.field === 'points' && columns.find(col => col.translationKey === 'ppg')?.getValue
      ? columns.find(col => col.translationKey === 'ppg')!.getValue!(b)
      : b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Actions</th>
            <th>Photo</th>
            <th>Statut</th>
            {columns.map((column) => (
              <th
                key={`${column.field}-${column.translationKey}`}
                onClick={() => column.sortable && handleSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedPlayers.map((player) => (
            <tr key={player.playerid}>
              <td>
                <button
                  className="btn btn-sm btn-success d-flex align-items-center"
                  onClick={() => onAddToProtection(player)}
                >
                  <Plus size={16} className="me-1" />
                  Protéger
                </button>
              </td>
              <td>
                <img 
                  src={getPlayerImageUrl(player)} 
                  alt={player.skaterfullname}
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  className="border"
                  onError={() => handleImageError(player.playerid)}
                />
              </td>
              <td>
                <span className={`badge ${player.isnhl ? 'bg-primary' : ''}`} style={!player.isnhl ? { backgroundColor: '#fd7e14' } : {}}>
                  {player.isnhl ? translate('main.club', language) : translate('reserves', language)}
                </span>
              </td>
              {columns.map((column) => (
                <td key={`${player.playerid}-${column.field}-${column.translationKey}`}>
                  {getCellValue(player, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}